/**
 * Heldenstatus unten in der Mitte (VISUAL_REFERENCE Abschnitt 17 und 18).
 *
 * Aufbau von links nach rechts:
 *
 *   rundes Portraet mit Level | HP und Ressource | Passive | kleine Slots
 *
 * Das Portraet ist noch ein Platzhalter aus Farbe und Anfangsbuchstabe - echte
 * Bilder kommen spaeter. Wichtig ist jetzt der Aufbau: ein runder Anker links,
 * daneben die Balken, und die Slots deutlich kleiner als die Kampfknoepfe
 * (Abschnitt 18: Faehigkeiten sind wichtiger als Verbrauchsgegenstaende).
 *
 * Grundsatz aus dem Masterprompt (Abschnitt 40): Zu jedem Balken steht immer
 * auch die Zahl. Farbe allein traegt nie eine Information.
 */

import { getHero, getHeroXpFactor } from '@dueline/game-data';
import { getLevelProgress } from '@dueline/game-rules';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { ItemSlots } from './ItemSlots.js';
import { LINEBOUND_BILDER } from './menueBilder.js';

/** Ein Balken mit Beschriftung, Zahl und Farbe. */
function Bar({
  label,
  value,
  max,
  variant,
}: {
  label: string;
  value: number;
  max: number;
  variant: 'hero' | 'mana';
}) {
  const anteil = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const kritisch = variant === 'hero' && anteil <= 0.3;

  /*
   * Die Beschriftung steht seit dem 27.09.2026 IM Balken, nicht daneben:
   * "HP 400 / 900" statt "400 / 900" mit einem Wort dahinter. Der Kasten wird
   * dadurch ruhiger, und der Wert bleibt da, wo man ihn sucht.
   */
  return (
    <div className="dl-herostatus__bar">
      <div className="dl-herostatus__bar-track">
        <div
          className={`dl-bar__fill dl-bar__fill--${variant}${kritisch ? ' dl-bar__fill--critical' : ''}`}
          style={{ width: `${anteil * 100}%` }}
        />
        <span className="dl-herostatus__bar-value">
          <span className="dl-herostatus__bar-name">{label}</span> {Math.ceil(value)} /{' '}
          {Math.round(max)}
        </span>
      </div>
    </div>
  );
}

/**
 * Die Passive des gespielten Heroes - jeder hat eine andere.
 *
 * Gezeigt wird nur die eigene: Eine leere Leiste bei allen anderen waere nur
 * im Weg (Abschnitt 48: die Mitte gehoert dem Spiel).
 */
/** Kurze Zustandsmeldungen: versteckt, Barrage, Schadensreduktion. */
function StateChips({ snapshot }: { snapshot: MatchSnapshot }) {
  const chips: { key: string; text: string; warnung?: boolean }[] = [];

  if (snapshot.hiddenFor > 0) {
    chips.push({ key: 'hidden', text: `${t.hidden.label} ${snapshot.hiddenFor.toFixed(1)}s` });
  }
  if (snapshot.channelSeconds > 0) {
    chips.push({ key: 'channel', text: t.channel.label, warnung: true });
  }

  if (chips.length === 0) return null;

  return (
    <div className="dl-herostatus__chips">
      {chips.map((chip) => (
        <span key={chip.key} className={'dl-chip' + (chip.warnung ? ' dl-chip--warning' : '')}>
          {chip.text}
        </span>
      ))}
    </div>
  );
}

/**
 * Das Sanctuary (Abschnitt 5f) als Schild UEBER dem Heldenkasten
 * (Rueckmeldung vom 13.10.2026). Vorher stand es als kleine Zeile unter
 * Leben und Mana und ging dort unter.
 *
 * Drei der fuenf Zustaende sind sichtbar. "draussen" ist der Normalfall, und
 * "blockiert" heisst nur, dass er gerade im Tor steht und es erst verlassen
 * muss - beides braucht keine Meldung.
 */
function SanctuarySchild({ snapshot }: { snapshot: MatchSnapshot }) {
  const sekunden = `${snapshot.sanctuarySeconds.toFixed(1)}s`;
  let text: string;
  let zeit: string | null = null;
  let art: 'unterwegs' | 'drinnen';
  if (snapshot.sanctuary === 'betritt') {
    text = t.sanctuary.entering;
    zeit = sekunden;
    art = 'unterwegs';
  } else if (snapshot.sanctuary === 'drinnen') {
    text = t.sanctuary.inside;
    art = 'drinnen';
  } else if (snapshot.sanctuary === 'verlaesst') {
    text = t.sanctuary.leaving;
    zeit = sekunden;
    art = 'unterwegs';
  } else {
    return null;
  }
  // Die Zeit steht getrennt: Die Grossschrift machte sonst aus "1.4s" ein "1.4S".
  return (
    <div className={`dl-sanctuary-schild dl-sanctuary-schild--${art}`} role="status">
      <span className="dl-sanctuary-schild__text">{text}</span>
      {zeit && <span className="dl-sanctuary-schild__zeit">{zeit}</span>}
    </div>
  );
}

export function HeroStatus({
  snapshot,
  onOpenShop,
}: {
  snapshot: MatchSnapshot;
  onOpenShop: () => void;
}) {
  const hero = getHero(snapshot.heroId);
  const portraet = hero ? LINEBOUND_BILDER[hero.linebound.toLowerCase()] : undefined;
  // Der Vanguard braucht 5 Prozent weniger Erfahrung je Level (v0.48) -
  // der Balken muss dieselbe Leiter benutzen wie der Server.
  const fortschritt = getLevelProgress(snapshot.xp, getHeroXpFactor(snapshot.heroId));
  const ressource = hero?.resource === 'energy' ? t.resource.energy : t.resource.mana;

  return (
    <div className="dl-herostatus">
      {snapshot.heroAlive && <SanctuarySchild snapshot={snapshot} />}

      {/*
        Portraet: seit dem 15.10.2026 das Bild des Linebound - dasselbe wie
        in der Auswahl, auf das Gesicht zugeschnitten. Fehlt eines, steht
        wie bisher der Anfangsbuchstabe da.
      */}
      {/*
        Die Huelle traegt das Level-Abzeichen AUSSERHALB des Kreises: Der Kreis
        schneidet ab, was ueber seinen Rand ragt, und das Abzeichen verschwand
        darin halb (Rueckmeldung vom 15.10.2026). Jetzt liegt es darueber.
      */}
      <div className="dl-herostatus__portrait-huelle">
        <div
          className="dl-herostatus__portrait"
          title={hero?.linebound ?? hero?.name ?? snapshot.heroId}
        >
          {portraet ? (
            <picture
              className={`dl-herostatus__portrait-bild dl-herostatus__portrait-bild--${hero?.linebound.toLowerCase()}`}
              aria-hidden="true"
            >
              <source type="image/avif" srcSet={portraet.avif} sizes="120px" />
              <source type="image/webp" srcSet={portraet.webp} sizes="120px" />
              <img src={portraet.ersatz} alt="" decoding="async" />
            </picture>
          ) : (
            <span className="dl-herostatus__portrait-letter" aria-hidden="true">
              {(hero?.name ?? '?').slice(0, 1)}
            </span>
          )}
          <span
            className="dl-herostatus__xp"
            style={{ width: `${Math.max(0, Math.min(1, fortschritt.ratio)) * 100}%` }}
          />
        </div>
        <span className="dl-herostatus__level">{snapshot.level}</span>
      </div>

      {snapshot.heroAlive ? (
        <>
          {/*
            Reihenfolge seit dem 27.09.2026: Portraet, dann die Slots, dann die
            Balken. Damit liegen Leben und Mana so weit rechts wie moeglich -
            also direkt neben den Trankkreisen. Genau das ist der Ablauf im
            Kampf: Balken kurz, Trank druecken.

            Das Portraet bleibt ganz links. Es sagt "dieser Kasten bin ich" und
            gehoert deshalb an den Anfang (Produktentscheidung vom 27.09.2026).
          */}
          <ItemSlots snapshot={snapshot} onOpenShop={onOpenShop} />

          <div className="dl-herostatus__bars">
            <Bar
              label={t.resource.hitPoints}
              value={snapshot.heroHp}
              max={snapshot.heroMaxHp}
              variant="hero"
            />
            <Bar label={ressource} value={snapshot.mana} max={snapshot.maxMana} variant="mana" />
            <StateChips snapshot={snapshot} />
          </div>
        </>
      ) : (
        <div className="dl-herostatus__dead">
          <span className="dl-herostatus__dead-label">{t.hud.dead}</span>
          <span className="dl-herostatus__dead-value">
            {t.hud.respawn} {Math.ceil(snapshot.respawnInSeconds)}s
          </span>
        </div>
      )}
    </div>
  );
}
